import React, { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { FinanceGranularity, FinanceTimeseriesBucket } from '../../../services/financeReportsService';
import { financePalette, formatBucket, formatMoney, formatMoneyFull } from './chartConfig';
import { useAuth } from '../../../context/AuthContext';

type SeriesKey = 'fees' | 'revenue' | 'fines' | 'expenses';

interface TrendChartProps {
  fees: FinanceTimeseriesBucket[] | null;
  revenue: FinanceTimeseriesBucket[] | null;
  expenses: FinanceTimeseriesBucket[] | null;
  fines: FinanceTimeseriesBucket[] | null;
  granularity: FinanceGranularity;
  height?: number;
}

/** Plot order and names — money in first, money out last. */
const SERIES: Array<{ key: SeriesKey; label: string }> = [
  { key: 'fees', label: 'Fee collections' },
  { key: 'revenue', label: 'Other income' },
  { key: 'fines', label: 'Fines collected' },
  { key: 'expenses', label: 'Expenses' },
];
const SERIES_LABEL = Object.fromEntries(SERIES.map(s => [s.key, s.label])) as Record<string, string>;

/**
 * Money in (fee collections, other income, fines) against money out
 * (approved expenses) over the selected window/granularity. Series are
 * unioned on the bucket key so a missing side plots as 0, not as a gap that
 * would mislead. Colours match the KPI tiles.
 */
const TrendChart: React.FC<TrendChartProps> = ({ fees, revenue, expenses, fines, granularity, height = 300 }) => {
  const theme = useTheme();
  const { currency } = useAuth();
  const palette = financePalette(theme);

  const data = useMemo(() => {
    const input: Record<SeriesKey, FinanceTimeseriesBucket[] | null> = { fees, revenue, fines, expenses };
    const map = new Map<string, Record<string, number | string>>();
    for (const { key } of SERIES) {
      for (const row of input[key] || []) {
        map.set(row.bucket, { ...(map.get(row.bucket) || { bucket: row.bucket }), [key]: row.amount });
      }
    }
    return Array.from(map.values())
      .sort((a, b) => String(a.bucket).localeCompare(String(b.bucket)))
      .map(r => ({
        ...Object.fromEntries(SERIES.map(({ key }) => [key, (r[key] as number) || 0])),
        bucket: r.bucket as string,
        label: formatBucket(r.bucket as string, granularity),
      }));
  }, [fees, revenue, expenses, fines, granularity]);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
        <defs>
          {SERIES.map(({ key }) => (
            <linearGradient key={key} id={`fin-${key}-fill`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={palette[key]} stopOpacity={0.32} />
              <stop offset="100%" stopColor={palette[key]} stopOpacity={0.02} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 4" stroke={palette.gridStroke} vertical={false} />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 11, fill: palette.axisTick }}
          axisLine={{ stroke: palette.gridStroke }}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: palette.axisTick }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(v) => formatMoney(v, currency)}
          width={70}
        />
        <Tooltip
          // Keep the tooltip above any sibling ChartCard when the pointer
          // hovers near the chart's right edge.
          wrapperStyle={{ zIndex: 1200, pointerEvents: 'none' }}
          contentStyle={{
            background: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 8,
            fontSize: 12,
            ...(theme.palette.mode === 'dark' && { color: theme.palette.text.primary }),
            boxShadow: theme.palette.mode === 'dark'
              ? '0 6px 20px rgba(0,0,0,0.5)'
              : '0 4px 16px rgba(0,0,0,0.08)',
          }}
          labelStyle={{ color: theme.palette.text.secondary, marginBottom: 6, fontWeight: 600 }}
          itemStyle={theme.palette.mode === 'dark' ? { color: theme.palette.text.primary } : undefined}
          formatter={(value: number, name: string) => [formatMoneyFull(value, currency), SERIES_LABEL[name] || name]}
        />
        <Legend
          verticalAlign="top"
          align="right"
          iconType="circle"
          wrapperStyle={{ fontSize: 12, paddingBottom: 8 }}
          formatter={(v) => SERIES_LABEL[v] || v}
        />
        {SERIES.map(({ key }) => (
          <Area
            key={key}
            type="monotone"
            dataKey={key}
            stroke={palette[key]}
            strokeWidth={2}
            fill={`url(#fin-${key}-fill)`}
            activeDot={{ r: 4, strokeWidth: 2 }}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
};

// Empty-state fallback so consumers don't need to know the shape.
export const TrendChartEmpty: React.FC = () => (
  <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <Typography variant="body2" color="text.disabled">No data for this window</Typography>
  </Box>
);

export default TrendChart;
